import { useState } from 'react';
import { css } from '@emotion/react';
import { UseNavigateResult } from '@tanstack/react-router';
import { Authn } from '@frontend/auth';
import { AuthStorage, localStorageHelper, SsoTenant } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { sentry } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import { BackIcon, Button, FormRow, Heading, Text, TextField, TextLink, useForm } from '@frontend/design-system';
import { getSsoConnection } from '../services';
import { FormWrap } from './form-wrap';
import { SignInError, SignInErrorTypeKey } from './sign-in-error.component';

interface Props<T extends string> {
  auth: Authn;
  tenant?: string;
  navigate: UseNavigateResult<T>;
}

export const SsoTenantLogin = <T extends string>({ auth, tenant, navigate }: Props<T>) => {
  const { t } = useTranslation('SignIn');
  const [errorCode, setErrorCode] = useState<SignInErrorTypeKey | undefined>(undefined);
  const [isError, setIsError] = useState(false);

  const { formProps, getFieldProps } = useForm({
    fields: {
      tenant: { type: 'text', required: true, value: tenant ?? '' },
    },
    onSubmit: async (data) => {
      try {
        const { clientId, redirectUri } = await getSsoConnection(data.tenant ?? '');
        const ssoTenant: SsoTenant = {
          tenant: data.tenant ?? '',
          clientId: clientId,
          redirectUri: redirectUri,
        };
        localStorageHelper.create(AuthStorage.sso_tenant, ssoTenant);
        auth.changeAuthMethod('oidc');
        await auth.signIn({
          clientId: clientId,
          redirectUri: redirectUri,
        });
      } catch (e: any) {
        console.error(e);
        setErrorCode('ssoTenantNotFound');
        setIsError(true);
        sentry.error({
          topic: 'auth',
          team: 'users-infra',
          error: e,
        });
      }
    },
  });

  return (
    <>
      <FormWrap>
        <Heading>{t('Log in with SSO')}</Heading>
        <Text>{t('Enter team identifier')}</Text>
        <form {...formProps} id='sso-tenant-login-form' data-testid='sso-tenant-login-form'>
          <FormRow
            css={css`
              text-align: initial;
              margin: ${theme.spacing(4, 0)};
            `}
          >
            <TextField {...getFieldProps('tenant')} label={t('Team Identifier')} />
          </FormRow>
          <Button
            variant='primary'
            size='large'
            type='submit'
            css={{ width: '100%' }}
            id='continue-to-sso-login-button'
          >
            {t('Continue to SSO Login')}
          </Button>
        </form>
        <TextLink
          css={css`
            display: flex;
            margin: ${theme.spacing(4, 0, 0)};
            cursor: pointer;
            text-align: left;
            width: 100%;
          `}
          onClick={() => {
            navigate({ to: '/' });
          }}
        >
          <>
            <BackIcon
              css={css`
                margin-right: ${theme.spacing(1)};
              `}
            />
            {t('Back to login')}
          </>
        </TextLink>
      </FormWrap>
      <div
        css={css`
          display: flex;
          flex-direction: column;
          justify-content: center;
          margin: 0 15%;
          min-width: 300px;
          max-width: 400px;
        `}
      >
        {isError ? <SignInError errorCode={errorCode} /> : null}
      </div>
    </>
  );
};
